import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import { API_URL } from '../../utils/runtimeConfig';
import { AppButton } from '../../components/ui';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';

const apiUrl = API_URL;
const DEFAULT_WEIGHTS = { rawPower: 1, language: 1, eventImpact: 1 };
const DEFAULT_EVENT_WINDOW_DAYS = 60;

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatNumber = (value) => Number(toNumber(value, 0)).toLocaleString();
const formatPercent = (value) => `${(toNumber(value, 0) * 100).toFixed(1)}%`;
const formatScore = (value) => toNumber(value, 0).toFixed(3);
const formatDelta = (value) => {
  const numeric = toNumber(value, 0);
  if (numeric > 0) return `+${numeric.toLocaleString()}`;
  return numeric.toLocaleString();
};
const RANK_OPTIONS = ['R5', 'R4', 'R3', 'R2', 'R1'];
const RANK_INDEX_BY_VALUE = RANK_OPTIONS.reduce((accumulator, rank, index) => {
  accumulator[rank] = index;
  return accumulator;
}, {});
const STATUS_SORT_ORDER = {
  moved: 0,
  unchanged: 1,
  overflow: 2,
  excluded: 3
};
const COMMANDER_TABLE_SORT_OPTIONS = [
  { key: 'fitScore', label: 'Fit Score' },
  { key: 'blendedRank', label: 'Blended Rank' },
  { key: 'commanderName', label: 'Commander Name' },
  { key: 'oldAllianceName', label: 'Old Alliance' },
  { key: 'proposedAllianceName', label: 'Proposed Alliance' },
  { key: 'proposedRank', label: 'Proposed Rank' },
  { key: 'status', label: 'Status' },
  { key: 'participationRate', label: 'Participation' },
  { key: 'eventImpactScore', label: 'Event Impact' }
];

const normalizeIdList = (values = []) => (
  Array.from(new Set((Array.isArray(values) ? values : [])
    .map((value) => String(value || '').trim())
    .filter(Boolean)))
);

const areIdListsEqual = (a = [], b = []) => {
  if (a.length !== b.length) return false;
  for (let index = 0; index < a.length; index += 1) {
    if (a[index] !== b[index]) return false;
  }
  return true;
};

const normalizeDisplacementChainPayload = (value = []) => (
  (Array.isArray(value) ? value : [])
    .map((entry) => ({
      commanderId: String(entry?.commanderId || '').trim(),
      targetAllianceId: String(entry?.targetAllianceId || '').trim()
    }))
    .filter((entry) => entry.commanderId && entry.targetAllianceId)
);

const getRankDirection = (currentRank, proposedRank) => {
  const current = String(currentRank || '').trim().toUpperCase();
  const proposed = String(proposedRank || '').trim().toUpperCase();
  if (!RANK_INDEX_BY_VALUE[current] && RANK_INDEX_BY_VALUE[current] !== 0) return 'same';
  if (!RANK_INDEX_BY_VALUE[proposed] && RANK_INDEX_BY_VALUE[proposed] !== 0) return 'same';
  if (RANK_INDEX_BY_VALUE[proposed] < RANK_INDEX_BY_VALUE[current]) return 'up';
  if (RANK_INDEX_BY_VALUE[proposed] > RANK_INDEX_BY_VALUE[current]) return 'down';
  return 'same';
};

const coercePlanSettings = (settings = {}) => ({
  weights: {
    rawPower: toNumber(settings?.weights?.rawPower, DEFAULT_WEIGHTS.rawPower),
    language: toNumber(settings?.weights?.language, DEFAULT_WEIGHTS.language),
    eventImpact: toNumber(settings?.weights?.eventImpact, DEFAULT_WEIGHTS.eventImpact)
  },
  eventWindowDays: toNumber(settings?.eventWindowDays, DEFAULT_EVENT_WINDOW_DAYS),
  stayBonusEnabled: settings?.stayBonusEnabled === true,
  capacities: settings?.capacities && typeof settings.capacities === 'object'
    ? settings.capacities
    : {},
  mergeTargets: settings?.mergeTargets && typeof settings.mergeTargets === 'object'
    ? settings.mergeTargets
    : {},
  sourceAllianceIds: Array.isArray(settings?.sourceAllianceIds)
    ? settings.sourceAllianceIds
    : [],
  targetAllianceIds: Array.isArray(settings?.targetAllianceIds)
    ? settings.targetAllianceIds
    : []
});

const sortTargetsByRank = (targets = []) => {
  return [...targets].sort((a, b) => Number(a?.orderRank || 99999) - Number(b?.orderRank || 99999));
};

const ConsolidationPlannerTab = ({ nationA, nationB }) => {

  const nationIds = useMemo(
    () => [nationA, nationB].filter(Boolean),
    [nationA, nationB]
  );
  const nationIdsParam = useMemo(() => nationIds.join(','), [nationIds]);

  const [plan, setPlan] = useState(null);
  const [preview, setPreview] = useState(null);
  const [preferences, setPreferences] = useState({ hiddenCommanderIds: [], showOnlyIncomingTargets: false });
  const [settingsDraft, setSettingsDraft] = useState({
    weights: DEFAULT_WEIGHTS,
    eventWindowDays: DEFAULT_EVENT_WINDOW_DAYS,
    stayBonusEnabled: false,
    capacities: {},
    mergeTargets: {},
    sourceAllianceIds: [],
    targetAllianceIds: []
  });

  const [isLoadingPlan, setIsLoadingPlan] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isRunningPreview, setIsRunningPreview] = useState(false);
  const [isResettingPlan, setIsResettingPlan] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showHiddenRows, setShowHiddenRows] = useState(false);
  const [commanderTableSearch, setCommanderTableSearch] = useState('');
  const [commanderTableStatusFilter, setCommanderTableStatusFilter] = useState('all');
  const [commanderTableOldAllianceFilter, setCommanderTableOldAllianceFilter] = useState('all');
  const [commanderTableProposedAllianceFilter, setCommanderTableProposedAllianceFilter] = useState('all');
  const [commanderTableSortKey, setCommanderTableSortKey] = useState('fitScore');
  const [commanderTableSortDirection, setCommanderTableSortDirection] = useState('desc');

  const [notesModal, setNotesModal] = useState({
    open: false,
    entityType: '',
    entityId: '',
    title: ''
  });
  const [notes, setNotes] = useState([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [creatingNote, setCreatingNote] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [newNoteVisibleToOthers, setNewNoteVisibleToOthers] = useState(true);
  const [editingNoteId, setEditingNoteId] = useState('');
  const [editingNoteText, setEditingNoteText] = useState('');
  const [editingNoteVisibleToOthers, setEditingNoteVisibleToOthers] = useState(true);
  const [overrideModal, setOverrideModal] = useState(null);

  const applyPayload = (payload = {}) => {
    if (payload?.plan) {
      setPlan(payload.plan);
      setSettingsDraft(coercePlanSettings(payload.plan.settings));
    }
    if (payload?.preview) {
      setPreview(payload.preview);
    }
    if (payload?.preferences) {
      setPreferences({
        hiddenCommanderIds: Array.isArray(payload.preferences.hiddenCommanderIds)
          ? payload.preferences.hiddenCommanderIds
          : [],
        showOnlyIncomingTargets: payload.preferences.showOnlyIncomingTargets === true
      });
    }
  };

  const loadPlan = async () => {
    if (nationIds.length !== 2) {
      setPlan(null);
      setPreview(null);
      return;
    }

    setIsLoadingPlan(true);
    setErrorMessage('');
    try {
      const response = await axiosInstance.get(`${apiUrl}/alliances/assignment/plan`, {
        params: { nationIds: nationIdsParam }
      });
      applyPayload(response?.data || {});
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || error?.message || 'Failed to load consolidation plan.'
      );
    } finally {
      setIsLoadingPlan(false);
    }
  };

  useEffect(() => {
    loadPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nationIdsParam]);

  const targets = useMemo(() => sortTargetsByRank(preview?.targets || []), [preview]);
  const allRows = useMemo(() => (Array.isArray(preview?.rows) ? preview.rows : []), [preview]);
  const hiddenCommanderIdSet = useMemo(
    () => new Set((preferences?.hiddenCommanderIds || []).map((id) => String(id))),
    [preferences]
  );

  const visibleRows = useMemo(() => {
    if (showHiddenRows) return allRows;
    return allRows.filter((row) => !hiddenCommanderIdSet.has(String(row.commanderId)));
  }, [allRows, hiddenCommanderIdSet, showHiddenRows]);

  const oldAllianceFilterOptions = useMemo(() => {
    const byId = new Map();
    visibleRows.forEach((row) => {
      const allianceId = String(row?.oldAllianceId || '').trim();
      const allianceName = String(row?.oldAllianceName || '').trim() || 'Unassigned';
      const key = allianceId || '__none';
      if (!byId.has(key)) {
        byId.set(key, {
          value: key,
          label: allianceName
        });
      }
    });
    return Array.from(byId.values()).sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [visibleRows]);

  const proposedAllianceFilterOptions = useMemo(() => {
    const byId = new Map();
    visibleRows.forEach((row) => {
      const allianceId = String(row?.proposedAllianceId || '').trim();
      const allianceName = String(row?.proposedAllianceName || '').trim() || 'Unassigned';
      const key = allianceId || '__none';
      if (!byId.has(key)) {
        byId.set(key, {
          value: key,
          label: allianceName
        });
      }
    });
    return Array.from(byId.values()).sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [visibleRows]);

  const filteredSortedRows = useMemo(() => {
    const normalizedSearch = String(commanderTableSearch || '').trim().toLowerCase();
    const baseRows = visibleRows.filter((row) => {
      if (commanderTableStatusFilter !== 'all' && String(row?.status || '') !== commanderTableStatusFilter) {
        return false;
      }

      if (commanderTableOldAllianceFilter !== 'all') {
        const oldAllianceKey = String(row?.oldAllianceId || '').trim() || '__none';
        if (oldAllianceKey !== commanderTableOldAllianceFilter) {
          return false;
        }
      }

      if (commanderTableProposedAllianceFilter !== 'all') {
        const proposedAllianceKey = String(row?.proposedAllianceId || '').trim() || '__none';
        if (proposedAllianceKey !== commanderTableProposedAllianceFilter) {
          return false;
        }
      }

      if (!normalizedSearch) return true;
      const searchBlob = [
        row?.commanderName,
        row?.oldAllianceName,
        row?.proposedAllianceName,
        row?.status,
        row?.language,
        row?.proposedRank,
        row?.currentRank
      ].map((value) => String(value || '').toLowerCase()).join(' ');
      return searchBlob.includes(normalizedSearch);
    });

    const multiplier = commanderTableSortDirection === 'asc' ? 1 : -1;
    const getRankValue = (rankValue) => {
      const normalizedRank = String(rankValue || '').trim().toUpperCase();
      if (Object.prototype.hasOwnProperty.call(RANK_INDEX_BY_VALUE, normalizedRank)) {
        return RANK_INDEX_BY_VALUE[normalizedRank];
      }
      return RANK_OPTIONS.length;
    };

    const sortValueForRow = (row) => {
      switch (commanderTableSortKey) {
        case 'blendedRank':
        {
          const blended = Number(row?.blendedRank);
          return Number.isFinite(blended) ? blended : Number.MAX_SAFE_INTEGER;
        }
        case 'commanderName':
          return String(row?.commanderName || '').toLowerCase();
        case 'oldAllianceName':
          return String(row?.oldAllianceName || '').toLowerCase();
        case 'proposedAllianceName':
          return String(row?.proposedAllianceName || '').toLowerCase();
        case 'proposedRank':
          return getRankValue(row?.proposedRank || row?.currentRank);
        case 'status':
          return toNumber(STATUS_SORT_ORDER[row?.status], Number.MAX_SAFE_INTEGER);
        case 'participationRate':
          return toNumber(row?.participationRate, 0);
        case 'eventImpactScore':
          return toNumber(row?.eventImpactScore, 0);
        case 'fitScore':
        default:
          return toNumber(row?.fitScore, 0);
      }
    };

    return [...baseRows].sort((a, b) => {
      const left = sortValueForRow(a);
      const right = sortValueForRow(b);

      if (typeof left === 'string' && typeof right === 'string') {
        const result = left.localeCompare(right);
        if (result !== 0) return result * multiplier;
      } else {
        const leftNumber = toNumber(left, 0);
        const rightNumber = toNumber(right, 0);
        if (leftNumber !== rightNumber) {
          return (leftNumber - rightNumber) * multiplier;
        }
      }

      return String(a?.commanderName || '').localeCompare(String(b?.commanderName || ''));
    });
  }, [
    commanderTableOldAllianceFilter,
    commanderTableProposedAllianceFilter,
    commanderTableSearch,
    commanderTableSortDirection,
    commanderTableSortKey,
    commanderTableStatusFilter,
    visibleRows
  ]);

  const visibleTargets = useMemo(() => {
    if (!preferences?.showOnlyIncomingTargets) return targets;
    return targets.filter((target) => Number(target?.incomingCount || 0) > 0);
  }, [targets, preferences]);
  const allianceOptions = useMemo(() => {
    const map = new Map();

    (preview?.availableAlliances || []).forEach((alliance) => {
      const allianceId = String(alliance?.allianceId || '').trim();
      if (!allianceId) return;
      map.set(allianceId, {
        allianceId,
        displayName: alliance?.displayName || alliance?.acronym || alliance?.name || allianceId
      });
    });

    targets.forEach((target) => {
      const allianceId = String(target?.allianceId || '').trim();
      if (!allianceId) return;
      if (!map.has(allianceId)) {
        map.set(allianceId, {
          allianceId,
          displayName: target?.displayName || target?.name || allianceId
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => String(a.displayName).localeCompare(String(b.displayName)));
  }, [preview, targets]);

  const allianceLabelById = useMemo(() => {
    const map = {};
    allianceOptions.forEach((option) => {
      map[option.allianceId] = option.displayName;
    });
    return map;
  }, [allianceOptions]);

  const selectedSourceAllianceValues = useMemo(() => {
    const selected = new Set((settingsDraft.sourceAllianceIds || []).map((id) => String(id)));
    return allianceOptions.filter((option) => selected.has(String(option.allianceId)));
  }, [allianceOptions, settingsDraft.sourceAllianceIds]);

  const scopeDrivenTargetAllianceIds = useMemo(() => {
    const selectedSourceIds = normalizeIdList(settingsDraft.sourceAllianceIds);
    if (selectedSourceIds.length > 0) {
      return selectedSourceIds;
    }
    return normalizeIdList(allianceOptions.map((option) => option.allianceId));
  }, [allianceOptions, settingsDraft.sourceAllianceIds]);

  useEffect(() => {
    setSettingsDraft((prev) => {
      const desiredTargetIds = scopeDrivenTargetAllianceIds;
      const desiredTargetIdSet = new Set(desiredTargetIds);
      const existingTargetIds = normalizeIdList(prev.targetAllianceIds);
      const keptTargetIds = existingTargetIds.filter((id) => desiredTargetIdSet.has(id));
      const addedTargetIds = desiredTargetIds.filter((id) => !keptTargetIds.includes(id));
      const nextTargetAllianceIds = [...keptTargetIds, ...addedTargetIds];

      const currentMergeTargets = prev?.mergeTargets && typeof prev.mergeTargets === 'object'
        ? prev.mergeTargets
        : {};
      const nextMergeTargets = {};
      Object.entries(currentMergeTargets).forEach(([sourceId, rawTargetIds]) => {
        const normalizedSourceId = String(sourceId || '').trim();
        if (!normalizedSourceId || !desiredTargetIdSet.has(normalizedSourceId)) return;
        const normalizedTargetIds = normalizeIdList(rawTargetIds).filter((targetId) => desiredTargetIdSet.has(targetId)).slice(0, 2);
        if (!normalizedTargetIds.length) return;
        nextMergeTargets[normalizedSourceId] = normalizedTargetIds;
      });

      const targetIdsChanged = !areIdListsEqual(existingTargetIds, nextTargetAllianceIds);
      const mergeTargetsChanged = JSON.stringify(currentMergeTargets) !== JSON.stringify(nextMergeTargets);
      if (!targetIdsChanged && !mergeTargetsChanged) {
        return prev;
      }

      return {
        ...prev,
        targetAllianceIds: nextTargetAllianceIds,
        mergeTargets: nextMergeTargets
      };
    });
  }, [scopeDrivenTargetAllianceIds]);

  const targetPriorityAllianceOptions = useMemo(() => (
    normalizeIdList(settingsDraft.targetAllianceIds).map((allianceId) => ({
      allianceId,
      displayName: allianceLabelById[allianceId] || allianceId
    }))
  ), [allianceLabelById, settingsDraft.targetAllianceIds]);

  const consideredCommanderCount = useMemo(() => {
    const summaryCount = Number(preview?.summary?.consideredPlayers);
    if (Number.isFinite(summaryCount) && summaryCount >= 0) {
      return summaryCount;
    }
    return allRows.filter((row) => !row?.excluded).length;
  }, [allRows, preview]);

  const receivableTargetAllianceIds = useMemo(() => {
    let remaining = consideredCommanderCount;
    const receivableIds = [];

    targetPriorityAllianceOptions.forEach((option) => {
      const allianceId = String(option?.allianceId || '').trim();
      if (!allianceId) return;

      const targetFromPreview = targets.find((target) => String(target?.allianceId || '') === allianceId);
      const rawCapacity = Object.prototype.hasOwnProperty.call(settingsDraft.capacities || {}, allianceId)
        ? settingsDraft.capacities[allianceId]
        : targetFromPreview?.capacity;
      const capacity = Math.max(0, Math.floor(toNumber(rawCapacity, 0)));

      if (remaining > 0 && capacity > 0) {
        receivableIds.push(allianceId);
      }
      remaining -= capacity;
    });

    return receivableIds;
  }, [consideredCommanderCount, settingsDraft.capacities, targetPriorityAllianceOptions, targets]);

  const receivableTargetAllianceIdSet = useMemo(
    () => new Set(receivableTargetAllianceIds.map((id) => String(id))),
    [receivableTargetAllianceIds]
  );

  const mergeTargetDestinationOptions = useMemo(() => {
    const filtered = targetPriorityAllianceOptions.filter((option) => (
      receivableTargetAllianceIdSet.has(String(option.allianceId))
    ));
    return filtered.length > 0 ? filtered : targetPriorityAllianceOptions;
  }, [receivableTargetAllianceIdSet, targetPriorityAllianceOptions]);
  const mergeTargetDestinationIdSet = useMemo(
    () => new Set(mergeTargetDestinationOptions.map((option) => String(option.allianceId))),
    [mergeTargetDestinationOptions]
  );

  const upsertSettingsAndRefresh = async (payload) => {
    setIsSavingSettings(true);
    setErrorMessage('');
    try {
      const response = await axiosInstance.patch(`${apiUrl}/alliances/assignment/plan/settings`, {
        nationIds,
        ...payload
      });
      applyPayload(response?.data || {});
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || error?.message || 'Failed to update plan settings.'
      );
    } finally {
      setIsSavingSettings(false);
    }
  };

  const runPreview = async () => {
    if (!plan) return;
    setIsRunningPreview(true);
    setErrorMessage('');
    try {
      let runMode = 'keep_overrides';
      const hasOverrides = Array.isArray(plan?.overrides) && plan.overrides.length > 0;
      if (hasOverrides) {
        const keepOverrides = window.confirm(
          'Keep manual commander overrides for this preview?\n\nOK: Keep overrides\nCancel: Fresh recompute'
        );
        runMode = keepOverrides ? 'keep_overrides' : 'fresh';
      }

      const response = await axiosInstance.post(`${apiUrl}/alliances/assignment/plan/run`, {
        nationIds,
        runMode
      });
      if (response?.data?.preview) {
        setPreview(response.data.preview);
      }
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || error?.message || 'Failed to run planner preview.'
      );
    } finally {
      setIsRunningPreview(false);
    }
  };

  const onSaveSettings = async () => {
    const sanitizedMergeTargets = {};
    Object.entries(settingsDraft.mergeTargets || {}).forEach(([sourceAllianceId, rawTargetIds]) => {
      const sourceId = String(sourceAllianceId || '').trim();
      if (!sourceId) return;
      const targetIds = normalizeIdList(rawTargetIds)
        .filter((targetId) => mergeTargetDestinationIdSet.has(String(targetId)))
        .slice(0, 2);
      if (!targetIds.length) return;
      sanitizedMergeTargets[sourceId] = targetIds;
    });

    await upsertSettingsAndRefresh({
      weights: settingsDraft.weights,
      eventWindowDays: settingsDraft.eventWindowDays,
      stayBonusEnabled: settingsDraft.stayBonusEnabled,
      capacities: settingsDraft.capacities,
      mergeTargets: sanitizedMergeTargets,
      sourceAllianceIds: settingsDraft.sourceAllianceIds,
      targetAllianceIds: settingsDraft.targetAllianceIds
    });
  };

  const resetPlan = async () => {
    const confirmed = window.confirm(
      'Reset consolidation plan for this nation pair?\n\nThis will delete planner settings, overrides, exclusions, notes, and hidden-row preferences.'
    );
    if (!confirmed) return;

    setIsResettingPlan(true);
    setErrorMessage('');
    try {
      await axiosInstance.delete(`${apiUrl}/alliances/assignment/plan`, {
        data: { nationIds }
      });
      await loadPlan();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || error?.message || 'Failed to reset consolidation plan.');
    } finally {
      setIsResettingPlan(false);
    }
  };

  const moveTargetAlliance = (allianceId, direction) => {
    setSettingsDraft((prev) => {
      const next = [...(prev.targetAllianceIds || [])];
      const index = next.indexOf(allianceId);
      if (index < 0) return prev;
      const nextIndex = direction === 'up' ? index - 1 : index + 1;
      if (nextIndex < 0 || nextIndex >= next.length) return prev;
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return {
        ...prev,
        targetAllianceIds: next
      };
    });
  };

  const updateMergeTargetForAlliance = (sourceAllianceId, slotIndex, value) => {
    setSettingsDraft((prev) => {
      const currentBySource = prev?.mergeTargets && typeof prev.mergeTargets === 'object'
        ? prev.mergeTargets
        : {};
      const currentTargets = Array.isArray(currentBySource[sourceAllianceId])
        ? [...currentBySource[sourceAllianceId]]
        : [];

      if (!value) {
        currentTargets.splice(slotIndex, 1);
      } else {
        currentTargets[slotIndex] = value;
      }

      const deduped = Array.from(new Set(currentTargets.map((id) => String(id || '').trim()).filter(Boolean))).slice(0, 2);
      const nextMergeTargets = { ...currentBySource };
      if (deduped.length > 0) {
        nextMergeTargets[sourceAllianceId] = deduped;
      } else {
        delete nextMergeTargets[sourceAllianceId];
      }

      return {
        ...prev,
        mergeTargets: nextMergeTargets
      };
    });
  };

  const updatePreference = async (nextPrefs) => {
    setErrorMessage('');
    try {
      const response = await axiosInstance.patch(`${apiUrl}/alliances/assignment/plan/preferences`, {
        nationIds,
        hiddenCommanderIds: nextPrefs.hiddenCommanderIds,
        showOnlyIncomingTargets: nextPrefs.showOnlyIncomingTargets
      });
      applyPayload(response?.data || {});
      if (!response?.data?.preferences) {
        setPreferences(nextPrefs);
      }
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || error?.message || 'Failed to update planner preferences.');
    }
  };

  const toggleShowOnlyIncomingTargets = () => {
    updatePreference({
      ...preferences,
      showOnlyIncomingTargets: !preferences.showOnlyIncomingTargets
    });
  };

  const toggleHideCommander = (commanderId) => {
    const stringId = String(commanderId || '');
    if (!stringId) return;
    const nextHidden = new Set((preferences?.hiddenCommanderIds || []).map((id) => String(id)));
    if (nextHidden.has(stringId)) {
      nextHidden.delete(stringId);
    } else {
      nextHidden.add(stringId);
    }

    updatePreference({
      ...preferences,
      hiddenCommanderIds: Array.from(nextHidden)
    });
  };

  const setCommanderOverride = async (commanderId, payload = {}) => {
    const targetAllianceId = String(payload?.targetAllianceId || '').trim();
    const applyAllianceOverride = payload?.applyAllianceOverride !== false;
    if (!commanderId || (applyAllianceOverride && !targetAllianceId)) return { ok: false };
    const displacementChain = normalizeDisplacementChainPayload(payload?.displacementChain);
    setErrorMessage('');
    try {
      const response = await axiosInstance.put(
        `${apiUrl}/alliances/assignment/plan/overrides/${commanderId}`,
        {
          nationIds,
          targetAllianceId,
          applyAllianceOverride,
          ...(Object.prototype.hasOwnProperty.call(payload || {}, 'targetRank')
            ? { targetRank: payload.targetRank }
            : {}),
          ...(displacementChain.length > 0 ? { displacementChain } : {})
        }
      );
      applyPayload(response?.data || {});
      return { ok: true };
    } catch (error) {
      const responseData = error?.response?.data;
      if (error?.response?.status === 409 && responseData?.code === 'OVERRIDE_CHAIN_UNRESOLVED' && applyAllianceOverride) {
        return { ok: false, unresolved: true, payload: responseData };
      }

      setErrorMessage(error?.response?.data?.message || error?.message || 'Failed to set commander override.');
      return { ok: false };
    }
  };

  const closeOverrideModal = () => {
    setOverrideModal(null);
  };

  const loadOverrideChainPreview = async ({
    requestingCommanderId,
    requestedTargetAllianceId,
    displacementChain = [],
    targetRank,
    hasTargetRankInput = false
  }) => {
    const response = await axiosInstance.post(`${apiUrl}/alliances/assignment/plan/overrides/preview`, {
      nationIds,
      requestingCommanderId,
      requestedTargetAllianceId,
      ...(hasTargetRankInput ? { targetRank } : {}),
      displacementChain: normalizeDisplacementChainPayload(displacementChain)
    });
    return response?.data || {};
  };

  const openOverrideModalForAlliance = async (row, nextTargetAllianceId) => {
    const targetAllianceId = String(nextTargetAllianceId || '').trim();
    if (!row?.commanderId || !targetAllianceId) return;
    const requestingCommanderId = String(row.commanderId || '').trim();
    const targetRank = row.rankOverrideApplied ? row.proposedRank : undefined;
    const modalSeed = {
      row,
      requestingCommanderId,
      requestedTargetAllianceId: targetAllianceId,
      targetRank,
      displacementChain: [],
      previewPayload: null,
      isLoadingPreview: true,
      isSubmitting: false
    };
    setOverrideModal(modalSeed);

    try {
      const previewPayload = await loadOverrideChainPreview({
        requestingCommanderId,
        requestedTargetAllianceId: targetAllianceId,
        ...(targetRank !== undefined ? { targetRank, hasTargetRankInput: true } : {}),
        displacementChain: []
      });
      setOverrideModal((prev) => {
        if (!prev) return prev;
        if (prev.requestingCommanderId !== requestingCommanderId) return prev;
        if (prev.requestedTargetAllianceId !== targetAllianceId) return prev;
        return {
          ...prev,
          previewPayload,
          displacementChain: normalizeDisplacementChainPayload(previewPayload?.displacementChain),
          isLoadingPreview: false,
          isSubmitting: false
        };
      });
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || error?.message || 'Failed to preview override chain.');
      setOverrideModal((prev) => (
        prev
          ? {
            ...prev,
            isLoadingPreview: false,
            isSubmitting: false
          }
          : prev
      ));
    }
  };

  const updateOverrideModalChain = async (chainIndex, patch = {}) => {
    if (!overrideModal) return;
    const normalizedChainIndex = Math.max(0, Number(chainIndex || 0));
    const currentChain = normalizeDisplacementChainPayload(overrideModal.displacementChain);
    const currentStep = (overrideModal?.previewPayload?.steps || [])
      .find((step) => Number(step?.chainIndex) === normalizedChainIndex) || null;
    const existingEntry = currentChain[normalizedChainIndex] || {};
    const nextCommanderId = String(
      patch?.commanderId !== undefined
        ? patch.commanderId
        : (existingEntry?.commanderId || currentStep?.selectedCommanderId || currentStep?.recommendedCommanderId || '')
    ).trim();
    const nextTargetAllianceId = String(
      patch?.targetAllianceId !== undefined
        ? patch.targetAllianceId
        : (existingEntry?.targetAllianceId || currentStep?.selectedDestinationAllianceId || currentStep?.recommendedDestinationAllianceId || '')
    ).trim();

    const nextChain = [...currentChain.slice(0, normalizedChainIndex)];
    if (nextCommanderId && nextTargetAllianceId) {
      nextChain.push({
        commanderId: nextCommanderId,
        targetAllianceId: nextTargetAllianceId
      });
    }

    setOverrideModal((prev) => (
      prev
        ? {
          ...prev,
          displacementChain: nextChain,
          isLoadingPreview: true,
          isSubmitting: false
        }
        : prev
    ));

    try {
      const previewPayload = await loadOverrideChainPreview({
        requestingCommanderId: overrideModal.requestingCommanderId,
        requestedTargetAllianceId: overrideModal.requestedTargetAllianceId,
        ...(overrideModal.targetRank !== undefined
          ? { targetRank: overrideModal.targetRank, hasTargetRankInput: true }
          : {}),
        displacementChain: nextChain
      });
      setOverrideModal((prev) => {
        if (!prev) return prev;
        if (prev.requestingCommanderId !== overrideModal.requestingCommanderId) return prev;
        if (prev.requestedTargetAllianceId !== overrideModal.requestedTargetAllianceId) return prev;
        return {
          ...prev,
          previewPayload,
          displacementChain: normalizeDisplacementChainPayload(previewPayload?.displacementChain),
          isLoadingPreview: false,
          isSubmitting: false
        };
      });
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || error?.message || 'Failed to recompute override chain.');
      setOverrideModal((prev) => (
        prev
          ? {
            ...prev,
            isLoadingPreview: false,
            isSubmitting: false
          }
          : prev
      ));
    }
  };

  const submitOverrideModal = async () => {
    if (!overrideModal?.requestingCommanderId || !overrideModal?.requestedTargetAllianceId) return;
    const previewPayload = overrideModal?.previewPayload || {};
    if (previewPayload?.isResolved !== true) {
      setErrorMessage('Complete the displacement chain until all capacity conflicts are resolved.');
      return;
    }

    setOverrideModal((prev) => (
      prev
        ? {
          ...prev,
          isSubmitting: true
        }
        : prev
    ));

    const result = await setCommanderOverride(overrideModal.requestingCommanderId, {
      targetAllianceId: overrideModal.requestedTargetAllianceId,
      applyAllianceOverride: true,
      ...(overrideModal.targetRank !== undefined ? { targetRank: overrideModal.targetRank } : {}),
      displacementChain: overrideModal.displacementChain
    });

    if (result?.ok) {
      setOverrideModal(null);
      return;
    }

    if (result?.unresolved && result?.payload) {
      setOverrideModal((prev) => (
        prev
          ? {
            ...prev,
            previewPayload: result.payload,
            displacementChain: normalizeDisplacementChainPayload(result.payload?.displacementChain),
            isSubmitting: false,
            isLoadingPreview: false
          }
          : prev
      ));
      return;
    }

    setOverrideModal((prev) => (
      prev
        ? {
          ...prev,
          isSubmitting: false
        }
        : prev
    ));
  };

  const clearCommanderOverride = async (commanderId) => {
    if (!commanderId) return;
    setErrorMessage('');
    try {
      const response = await axiosInstance.delete(
        `${apiUrl}/alliances/assignment/plan/overrides/${commanderId}`,
        {
          data: { nationIds }
        }
      );
      applyPayload(response?.data || {});
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || error?.message || 'Failed to remove commander override.');
    }
  };

  const toggleCommanderExclusion = async (row) => {
    if (!row?.commanderId) return;
    setErrorMessage('');
    try {
      const endpoint = `${apiUrl}/alliances/assignment/plan/exclusions/${row.commanderId}`;
      const response = row.excluded
        ? await axiosInstance.delete(endpoint, { data: { nationIds } })
        : await axiosInstance.put(endpoint, { nationIds });
      applyPayload(response?.data || {});
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || error?.message || 'Failed to update commander exclusion.');
    }
  };

  const fetchNotes = async (entityType, entityId) => {
    if (!entityType || !entityId) return;
    setIsLoadingNotes(true);
    try {
      const response = await axiosInstance.get(`${apiUrl}/alliances/assignment/plan/notes`, {
        params: {
          nationIds: nationIdsParam,
          entityType,
          entityId
        }
      });
      setNotes(Array.isArray(response?.data?.notes) ? response.data.notes : []);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || error?.message || 'Failed to load notes.');
      setNotes([]);
    } finally {
      setIsLoadingNotes(false);
    }
  };

  const openNotesModal = async ({ entityType, entityId, title }) => {
    setNotesModal({
      open: true,
      entityType,
      entityId,
      title
    });
    setNewNoteText('');
    setNewNoteVisibleToOthers(true);
    setEditingNoteId('');
    await fetchNotes(entityType, entityId);
  };

  const closeNotesModal = () => {
    setNotesModal({ open: false, entityType: '', entityId: '', title: '' });
    setNotes([]);
    setNewNoteText('');
    setEditingNoteId('');
  };

  const createNote = async () => {
    const text = String(newNoteText || '').trim();
    if (!text || !notesModal.entityId) return;

    setCreatingNote(true);
    try {
      await axiosInstance.post(`${apiUrl}/alliances/assignment/plan/notes`, {
        nationIds,
        entityType: notesModal.entityType,
        entityId: notesModal.entityId,
        text,
        visibleToOthers: newNoteVisibleToOthers
      });
      setNewNoteText('');
      setNewNoteVisibleToOthers(true);
      await fetchNotes(notesModal.entityType, notesModal.entityId);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || error?.message || 'Failed to create note.');
    } finally {
      setCreatingNote(false);
    }
  };

  const startEditNote = (note) => {
    setEditingNoteId(String(note?._id || ''));
    setEditingNoteText(String(note?.text || ''));
    setEditingNoteVisibleToOthers(note?.visibleToOthers !== false);
  };

  const cancelEditNote = () => {
    setEditingNoteId('');
    setEditingNoteText('');
    setEditingNoteVisibleToOthers(true);
  };

  const saveEditNote = async () => {
    if (!editingNoteId) return;
    const text = String(editingNoteText || '').trim();
    if (!text) return;

    try {
      await axiosInstance.patch(`${apiUrl}/alliances/assignment/plan/notes/${editingNoteId}`, {
        nationIds,
        text,
        visibleToOthers: editingNoteVisibleToOthers
      });
      cancelEditNote();
      await fetchNotes(notesModal.entityType, notesModal.entityId);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || error?.message || 'Failed to update note.');
    }
  };

  const deleteNote = async (noteId) => {
    if (!noteId) return;
    const confirmed = window.confirm('Delete this note?');
    if (!confirmed) return;

    try {
      await axiosInstance.delete(`${apiUrl}/alliances/assignment/plan/notes/${noteId}`, {
        data: { nationIds }
      });
      await fetchNotes(notesModal.entityType, notesModal.entityId);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || error?.message || 'Failed to delete note.');
    }
  };

  const summary = preview?.summary || {};

  if (nationIds.length !== 2) {
    return (
      <div className="md-card">
        <h2>Alliance Consolidation Planner</h2>
        <p>Select two nations with an active planning session to use the persistent consolidation planner.</p>
      </div>
    );
  }

  const isBusy = isLoadingPlan || isSavingSettings || isRunningPreview || isResettingPlan;

  return (
    <div className="md-planner-tab">
      <div className="md-card">
        <h2>Alliance Consolidation Planner</h2>
        <p>
          Persistent plan workspace for this merger session. Adjust settings, run previews,
          override assignments, exclude/hide commanders, and maintain alliance/commander notes.
        </p>

        <div className="md-planner-current-plan">
          <div className="md-planner-current-pill">
            <span>Overrides</span>
            <strong>{formatNumber((plan?.overrides || []).length)}</strong>
          </div>
          <div className="md-planner-current-pill">
            <span>Excluded</span>
            <strong>{formatNumber((plan?.exclusions || []).length)}</strong>
          </div>
          <div className="md-planner-current-pill">
            <span>Hidden (You)</span>
            <strong>{formatNumber((preferences?.hiddenCommanderIds || []).length)}</strong>
          </div>
          <div className="md-planner-current-pill">
            <span>Updated</span>
            <strong>{plan?.updatedAt ? new Date(plan.updatedAt).toLocaleString() : 'n/a'}</strong>
          </div>
        </div>

        <div className="md-planner-controls">
          <div className="md-planner-control">
            <label htmlFor="planner-weight-raw">Raw Power Weight</label>
            <input
              id="planner-weight-raw"
              type="range"
              min="0"
              max="10"
              step="0.5"
              value={settingsDraft.weights.rawPower}
              onChange={(event) => setSettingsDraft((prev) => ({
                ...prev,
                weights: {
                  ...prev.weights,
                  rawPower: Number(event.target.value)
                }
              }))}
            />
            <span>{settingsDraft.weights.rawPower.toFixed(1)}</span>
          </div>

          <div className="md-planner-control">
            <label htmlFor="planner-weight-language">Language Weight</label>
            <input
              id="planner-weight-language"
              type="range"
              min="0"
              max="10"
              step="0.5"
              value={settingsDraft.weights.language}
              onChange={(event) => setSettingsDraft((prev) => ({
                ...prev,
                weights: {
                  ...prev.weights,
                  language: Number(event.target.value)
                }
              }))}
            />
            <span>{settingsDraft.weights.language.toFixed(1)}</span>
          </div>

          <div className="md-planner-control">
            <label htmlFor="planner-weight-event">Event Impact Weight</label>
            <input
              id="planner-weight-event"
              type="range"
              min="0"
              max="10"
              step="0.5"
              value={settingsDraft.weights.eventImpact}
              onChange={(event) => setSettingsDraft((prev) => ({
                ...prev,
                weights: {
                  ...prev.weights,
                  eventImpact: Number(event.target.value)
                }
              }))}
            />
            <span>{settingsDraft.weights.eventImpact.toFixed(1)}</span>
          </div>

          <div className="md-planner-control md-planner-number-control">
            <label htmlFor="planner-event-window">Event Window (days)</label>
            <input
              id="planner-event-window"
              type="number"
              min="1"
              max="365"
              value={settingsDraft.eventWindowDays}
              onChange={(event) => setSettingsDraft((prev) => ({
                ...prev,
                eventWindowDays: event.target.value
              }))}
            />
          </div>

          <div className="md-planner-control">
            <MultiSelectDropdown
              label="Included Alliances (scope)"
              options={allianceOptions}
              selectedValues={selectedSourceAllianceValues}
              onChange={(nextOptions) => {
                const selectedIds = Array.from(
                  new Set((nextOptions || []).map((option) => String(option?.allianceId || '')).filter(Boolean))
                );
                setSettingsDraft((prev) => ({
                  ...prev,
                  sourceAllianceIds: selectedIds
                }));
              }}
              getOptionLabel={(option) => String(option?.displayName || option?.allianceId || '')}
              getOptionValue={(option) => String(option?.allianceId || '')}
              helperText="If empty, commanders from all alliances in both nations are considered."
            />
          </div>

          <label className="md-planner-toggle">
            <input
              type="checkbox"
              checked={settingsDraft.stayBonusEnabled}
              onChange={(event) => setSettingsDraft((prev) => ({
                ...prev,
                stayBonusEnabled: event.target.checked
              }))}
            />
            <span>Minimize churn (stay bonus)</span>
          </label>

          <label className="md-planner-toggle">
            <input
              type="checkbox"
              checked={preferences.showOnlyIncomingTargets === true}
              onChange={toggleShowOnlyIncomingTargets}
            />
            <span>Show only alliances with incoming proposals</span>
          </label>

          <label className="md-planner-toggle">
            <input
              type="checkbox"
              checked={showHiddenRows}
              onChange={(event) => setShowHiddenRows(event.target.checked)}
            />
            <span>Show hidden commanders</span>
          </label>
        </div>

        {targetPriorityAllianceOptions.length > 0 && (
          <div className="md-planner-priority-order">
            <h4>Target Priority Order</h4>
            <p>
              Driven by the Included Alliances scope. This order is used by preview assignment fallback,
              including merge overflow routing. Alliances below the capacity cut line remain in this list
              but are excluded as destination options.
            </p>
            <div className="md-planner-order-list">
              {targetPriorityAllianceOptions.map((option, index) => (
                <div key={option.allianceId} className="md-planner-order-row">
                  <span>{option.displayName}</span>
                  <div className="md-planner-order-actions">
                    <AppButton
                      className="md-secondary-button"
                      disabled={index === 0}
                      onClick={() => moveTargetAlliance(option.allianceId, 'up')}
                    >
                      Up
                    </AppButton>
                    <AppButton
                      className="md-secondary-button"
                      disabled={index === targetPriorityAllianceOptions.length - 1}
                      onClick={() => moveTargetAlliance(option.allianceId, 'down')}
                    >
                      Down
                    </AppButton>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="md-planner-actions">
          <AppButton className="md-primary-button" onClick={onSaveSettings} disabled={isBusy}>
            {isSavingSettings ? 'Saving Settings...' : 'Save Settings'}
          </AppButton>
          <AppButton className="md-secondary-button" onClick={runPreview} disabled={isBusy || !plan}>
            {isRunningPreview ? 'Running Preview...' : 'Run Preview'}
          </AppButton>
          <AppButton className="md-secondary-button" onClick={loadPlan} disabled={isBusy}>
            Reload Plan
          </AppButton>
          <AppButton className="md-danger-button" onClick={resetPlan} disabled={isBusy}>
            {isResettingPlan ? 'Resetting...' : 'Reset Plan'}
          </AppButton>
          {errorMessage ? <p className="md-planner-error">{errorMessage}</p> : null}
        </div>
      </div>

      {targets.length > 0 && (
        <div className="md-card">
          <h3>Target Alliances and Capacity</h3>
          <div className="md-planner-capacity-table-wrap">
            <table className="md-summary-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Alliance</th>
                  <th>Strength</th>
                  <th>Dominant Language</th>
                  <th>Capacity</th>
                  <th>Current Fill</th>
                  <th>Projected Fill</th>
                  <th>Fill Delta</th>
                  <th>Incoming</th>
                  <th>Outgoing</th>
                  <th>Projected Power</th>
                  <th>Merge Pair</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleTargets.map((target) => (
                  <tr key={target.allianceId}>
                    <td>{target.orderRank}</td>
                    <td>{target.displayName}</td>
                    <td>{formatScore(target.strengthScore)}</td>
                    <td>{target.dominantLanguage || 'Unknown'}</td>
                    <td>
                      <input
                        className="md-planner-capacity-input"
                        type="number"
                        min="0"
                        max="500"
                        value={Object.prototype.hasOwnProperty.call(settingsDraft.capacities, target.allianceId)
                          ? settingsDraft.capacities[target.allianceId]
                          : target.capacity}
                        onChange={(event) => {
                          const rawValue = event.target.value;
                          setSettingsDraft((prev) => ({
                            ...prev,
                            capacities: {
                              ...prev.capacities,
                              [target.allianceId]: rawValue
                            }
                          }));
                        }}
                      />
                    </td>
                    <td>{formatNumber(target.currentFill)}</td>
                    <td>{formatNumber(target.projectedFill)}</td>
                    <td>{formatDelta(target.projectedFillDelta)}</td>
                    <td>{formatNumber(target.incomingCount)}</td>
                    <td>{formatNumber(target.outgoingCount)}</td>
                    <td>{formatNumber(target.projectedBattlePower)}</td>
                    <td>
                      <div className="md-planner-merge-pair">
                        <select
                          className="md-planner-row-select"
                          value={Array.isArray(settingsDraft.mergeTargets?.[target.allianceId])
                            ? (
                              mergeTargetDestinationOptions.some((option) => option.allianceId === settingsDraft.mergeTargets[target.allianceId][0])
                                ? (settingsDraft.mergeTargets[target.allianceId][0] || '')
                                : ''
                            )
                            : ''}
                          onChange={(event) => updateMergeTargetForAlliance(target.allianceId, 0, event.target.value)}
                        >
                          <option value="">Primary</option>
                          {mergeTargetDestinationOptions.map((option) => (
                            <option key={`${target.allianceId}-primary-${option.allianceId}`} value={option.allianceId}>
                              {option.displayName}
                            </option>
                          ))}
                        </select>
                        <select
                          className="md-planner-row-select"
                          value={Array.isArray(settingsDraft.mergeTargets?.[target.allianceId])
                            ? (
                              mergeTargetDestinationOptions.some((option) => option.allianceId === settingsDraft.mergeTargets[target.allianceId][1])
                                ? (settingsDraft.mergeTargets[target.allianceId][1] || '')
                                : ''
                            )
                            : ''}
                          onChange={(event) => updateMergeTargetForAlliance(target.allianceId, 1, event.target.value)}
                          disabled={!receivableTargetAllianceIdSet.has(String(target.allianceId))}
                          title={!receivableTargetAllianceIdSet.has(String(target.allianceId))
                            ? 'Overflow destination disabled: this alliance is below the receiving-capacity cut line.'
                            : 'Overflow destination'}
                        >
                          <option value="">Overflow</option>
                          {mergeTargetDestinationOptions.map((option) => (
                            <option key={`${target.allianceId}-overflow-${option.allianceId}`} value={option.allianceId}>
                              {option.displayName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </td>
                    <td>
                      <AppButton
                        className="md-secondary-button"
                        onClick={() => openNotesModal({
                          entityType: 'alliance',
                          entityId: target.allianceId,
                          title: `Alliance Notes: ${target.displayName}`
                        })}
                      >
                        Notes
                      </AppButton>
                    </td>
                  </tr>
                ))}
                {visibleTargets.length === 0 && (
                  <tr>
                    <td colSpan={13}>No alliances currently have incoming proposals.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {preview && (
        <div className="md-card">
          <h3>Summary</h3>
          <div className="md-planner-summary-grid">
            <div className="md-planner-summary-item">
              <span className="md-planner-summary-label">Players</span>
              <strong>{formatNumber(summary.totalPlayers)}</strong>
            </div>
            <div className="md-planner-summary-item">
              <span className="md-planner-summary-label">Moved</span>
              <strong>{formatNumber(summary.movedCount)}</strong>
            </div>
            <div className="md-planner-summary-item">
              <span className="md-planner-summary-label">Unchanged</span>
              <strong>{formatNumber(summary.unchangedCount)}</strong>
            </div>
            <div className="md-planner-summary-item">
              <span className="md-planner-summary-label">Overflow</span>
              <strong>{formatNumber(summary.overflowCount)}</strong>
            </div>
            <div className="md-planner-summary-item">
              <span className="md-planner-summary-label">Excluded</span>
              <strong>{formatNumber(summary.excludedCount)}</strong>
            </div>
            <div className="md-planner-summary-item">
              <span className="md-planner-summary-label">Events in Window</span>
              <strong>{formatNumber(summary.eventsInWindow)}</strong>
            </div>
          </div>
        </div>
      )}

      {allRows.length > 0 && (
        <div className="md-card">
          <h3>Commander Assignment Preview</h3>
          <div className="md-planner-row-toolbar">
            <label className="md-planner-row-toolbar-item">
              <span>Search</span>
              <input
                type="text"
                value={commanderTableSearch}
                onChange={(event) => setCommanderTableSearch(event.target.value)}
                placeholder="Commander, alliance, rank, language"
              />
            </label>

            <label className="md-planner-row-toolbar-item">
              <span>Status</span>
              <select
                value={commanderTableStatusFilter}
                onChange={(event) => setCommanderTableStatusFilter(event.target.value)}
              >
                <option value="all">All</option>
                <option value="moved">Moved</option>
                <option value="unchanged">Unchanged</option>
                <option value="overflow">Overflow</option>
                <option value="excluded">Excluded</option>
              </select>
            </label>

            <label className="md-planner-row-toolbar-item">
              <span>Old Alliance</span>
              <select
                value={commanderTableOldAllianceFilter}
                onChange={(event) => setCommanderTableOldAllianceFilter(event.target.value)}
              >
                <option value="all">All</option>
                {oldAllianceFilterOptions.map((option) => (
                  <option key={`old-${option.value}`} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>

            <label className="md-planner-row-toolbar-item">
              <span>Proposed Alliance</span>
              <select
                value={commanderTableProposedAllianceFilter}
                onChange={(event) => setCommanderTableProposedAllianceFilter(event.target.value)}
              >
                <option value="all">All</option>
                {proposedAllianceFilterOptions.map((option) => (
                  <option key={`proposed-${option.value}`} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>

            <label className="md-planner-row-toolbar-item">
              <span>Sort By</span>
              <select
                value={commanderTableSortKey}
                onChange={(event) => setCommanderTableSortKey(event.target.value)}
              >
                {COMMANDER_TABLE_SORT_OPTIONS.map((option) => (
                  <option key={option.key} value={option.key}>{option.label}</option>
                ))}
              </select>
            </label>

            <label className="md-planner-row-toolbar-item">
              <span>Direction</span>
              <select
                value={commanderTableSortDirection}
                onChange={(event) => setCommanderTableSortDirection(event.target.value)}
              >
                <option value="desc">Desc</option>
                <option value="asc">Asc</option>
              </select>
            </label>
          </div>

          <div className="md-planner-table-wrap">
            <table className="md-summary-table">
              <thead>
                <tr>
                  <th>Commander</th>
                  <th>Old Alliance</th>
                  <th>Proposed Alliance</th>
                  <th>Proposed Rank</th>
                  <th>Status</th>
                  <th>Blended Rank</th>
                  <th>Language</th>
                  <th>Participation</th>
                  <th>Avg Score/Event</th>
                  <th>Event Impact</th>
                  <th>Fit Score</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSortedRows.map((row) => {
                  const isHidden = hiddenCommanderIdSet.has(String(row.commanderId));
                  const rankDirection = getRankDirection(row.currentRank, row.proposedRank);
                  const rankDirectionSymbol = rankDirection === 'up'
                    ? '↑'
                    : (rankDirection === 'down' ? '↓' : '-');
                  return (
                    <tr
                      key={row.commanderId}
                      className={[
                        row.excluded ? 'md-planner-row-excluded' : '',
                        isHidden ? 'md-planner-row-hidden' : ''
                      ].filter(Boolean).join(' ')}
                    >
                      <td>
                        <div className="md-planner-commander-cell">
                          <span>{row.commanderName}</span>
                          {row.proposedAllianceId && !row.excluded && !row.overflow ? (
                            <span
                              className={[
                                'md-planner-assignment-badge',
                                row.overrideApplied
                                  ? 'md-planner-assignment-badge-override'
                                  : 'md-planner-assignment-badge-proposed'
                              ].join(' ')}
                            >
                              {row.overrideApplied ? 'Override' : 'Proposed'}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <div className="md-planner-old-alliance-cell">
                          <span>{row.oldAllianceName || 'Unassigned'}</span>
                          <span className="md-planner-rank-pill">{row.currentRank || 'R1'}</span>
                        </div>
                      </td>
                      <td>
                        <select
                          className="md-planner-override-select"
                          value={row.excluded ? '' : (row.proposedAllianceId || '')}
                          onChange={(event) => {
                            const nextTargetAllianceId = String(event.target.value || '').trim();
                            if (!nextTargetAllianceId) return;
                            if (nextTargetAllianceId === String(row.proposedAllianceId || '').trim()) return;
                            openOverrideModalForAlliance(row, nextTargetAllianceId);
                          }}
                          disabled={row.excluded}
                        >
                          <option value="">Select target</option>
                          {targets.map((target) => (
                            <option key={target.allianceId} value={target.allianceId}>
                              {target.displayName}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <div className="md-planner-rank-cell">
                          <select
                            className="md-planner-rank-select"
                            value={row.rankOverrideApplied ? (row.proposedRank || '') : ''}
                            onChange={(event) => setCommanderOverride(row.commanderId, {
                              targetAllianceId: row.proposedAllianceId || row.oldAllianceId,
                              applyAllianceOverride: false,
                              targetRank: event.target.value
                            })}
                            disabled={row.excluded || row.overflow || !row.proposedAllianceId}
                          >
                            <option value="">{row.proposedRank || row.currentRank || 'R1'}</option>
                            {RANK_OPTIONS.map((rank) => (
                              <option key={rank} value={rank}>{rank}</option>
                            ))}
                          </select>
                          <span
                            className={[
                              'md-planner-rank-arrow-badge',
                              `md-planner-rank-arrow-${rankDirection}`
                            ].join(' ')}
                            title={`Rank change: ${row.currentRank || 'R1'} -> ${row.proposedRank || row.currentRank || 'R1'}`}
                          >
                            {rankDirectionSymbol}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={`md-planner-status md-planner-status-${row.status}`}>
                          {row.status}
                        </span>
                      </td>
                      <td>
                        {row.blendedRank === null || row.blendedRank === undefined
                          ? 'n/a'
                          : toNumber(row.blendedRank, 0).toFixed(2)}
                      </td>
                      <td>{row.language || 'Unknown'}</td>
                      <td>{formatPercent(row.participationRate)}</td>
                      <td>{formatNumber(row.averageScorePerEvent)}</td>
                      <td>{formatScore(row.eventImpactScore)}</td>
                      <td>{formatScore(row.fitScore)}</td>
                      <td>
                        <div className="md-planner-row-actions">
                          {(row.overrideApplied || row.rankOverrideApplied) ? (
                            <AppButton
                              className="md-secondary-button"
                              onClick={() => clearCommanderOverride(row.commanderId)}
                            >
                              Clear Overrides
                            </AppButton>
                          ) : null}

                          <AppButton
                            className="md-secondary-button"
                            onClick={() => toggleCommanderExclusion(row)}
                          >
                            {row.excluded ? 'Restore' : 'Exclude'}
                          </AppButton>

                          <AppButton
                            className="md-secondary-button"
                            onClick={() => toggleHideCommander(row.commanderId)}
                          >
                            {isHidden ? 'Unhide' : 'Hide'}
                          </AppButton>

                          <AppButton
                            className="md-secondary-button"
                            onClick={() => openNotesModal({
                              entityType: 'commander',
                              entityId: row.commanderId,
                              title: `Commander Notes: ${row.commanderName}`
                            })}
                          >
                            Notes
                          </AppButton>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredSortedRows.length === 0 && (
                  <tr>
                    <td colSpan={12}>No commanders are visible with the current filters.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {overrideModal && (
        <div className="md-planner-conflict-backdrop" onClick={closeOverrideModal}>
          <div className="md-planner-conflict-modal" onClick={(event) => event.stopPropagation()}>
            <div className="md-planner-conflict-header">
              <h3>Override Displacement Flow</h3>
              <AppButton className="md-secondary-button" onClick={closeOverrideModal}>
                Close
              </AppButton>
            </div>

            <p className="md-planner-conflict-message">
              Move <strong>{overrideModal?.row?.commanderName || 'selected commander'}</strong> from{' '}
              <strong>{overrideModal?.row?.proposedAllianceName || overrideModal?.row?.oldAllianceName || 'current alliance'}</strong>{' '}
              to <strong>{overrideModal?.requestedTargetAllianceId || 'target alliance'}</strong>.
            </p>

            {overrideModal?.isLoadingPreview ? (
              <p className="md-planner-conflict-message">Loading displacement options...</p>
            ) : (
              <>
                <div className="md-planner-conflict-summary">
                  <div>
                    <span>Status</span>
                    <strong>{overrideModal?.previewPayload?.isResolved ? 'Resolved' : 'Needs displacement'}</strong>
                  </div>
                  <div>
                    <span>Current Over-Cap Alliance</span>
                    <strong>{overrideModal?.previewPayload?.overCapacityAlliance?.displayName || 'None'}</strong>
                  </div>
                  <div>
                    <span>Over-Cap Amount</span>
                    <strong>{formatNumber(overrideModal?.previewPayload?.overCapacityAlliance?.exceedsBy || 0)}</strong>
                  </div>
                </div>

                {(overrideModal?.previewPayload?.blockedReason || '').trim() ? (
                  <p className="md-planner-error">{overrideModal.previewPayload.blockedReason}</p>
                ) : null}

                {(overrideModal?.previewPayload?.steps || []).length > 0 ? (
                  <div className="md-planner-conflict-chain">
                    <h4>Displacement Steps</h4>
                    {(overrideModal.previewPayload.steps || []).map((step) => (
                      <div key={`step-${step.chainIndex}`} className="md-planner-chain-step">
                        <p className="md-planner-conflict-message">
                          Step {Number(step.stepNumber || 0)}: free 1 seat in <strong>{step.requestedTargetAllianceName || step.requestedTargetAllianceId}</strong>
                          {' '}for <strong>{step.unresolvedCommanderName || step.unresolvedCommanderId}</strong>.
                        </p>
                        <div className="md-planner-conflict-fields">
                          <label>
                            <span>Displaced Commander</span>
                            <select
                              value={step.selectedCommanderId || ''}
                              onChange={(event) => {
                                const selectedCommanderId = String(event.target.value || '').trim();
                                setOverrideModal((prev) => {
                                  if (!prev) return prev;
                                  const nextPreviewPayload = prev.previewPayload
                                    ? {
                                      ...prev.previewPayload,
                                      steps: (prev.previewPayload.steps || []).map((entry) => (
                                        Number(entry?.chainIndex) === Number(step.chainIndex)
                                          ? {
                                            ...entry,
                                            selectedCommanderId,
                                            selectedDestinationAllianceId: '',
                                            isConfigured: false
                                          }
                                          : entry
                                      ))
                                    }
                                    : prev.previewPayload;
                                  const truncatedChain = normalizeDisplacementChainPayload(prev.displacementChain)
                                    .slice(0, Math.max(0, Number(step.chainIndex || 0)));
                                  return {
                                    ...prev,
                                    previewPayload: nextPreviewPayload,
                                    displacementChain: truncatedChain,
                                    isSubmitting: false
                                  };
                                });
                              }}
                            >
                              <option value="">Select commander</option>
                              {(step.displacementCandidates || []).map((candidate) => (
                                <option key={candidate.commanderId} value={candidate.commanderId}>
                                  {candidate.commanderName} ({candidate.proposedRank}, fit {formatScore(candidate.fitScore)})
                                </option>
                              ))}
                            </select>
                          </label>

                          <label>
                            <span>Destination Alliance</span>
                            <select
                              value={step.selectedDestinationAllianceId || ''}
                              onChange={(event) => {
                                const selectedTargetAllianceId = String(event.target.value || '').trim();
                                const fallbackCommanderId = String(
                                  step.selectedCommanderId ||
                                  step.recommendedCommanderId ||
                                  step.displacementCandidates?.[0]?.commanderId ||
                                  ''
                                ).trim();
                                updateOverrideModalChain(step.chainIndex, {
                                  commanderId: fallbackCommanderId,
                                  targetAllianceId: selectedTargetAllianceId
                                });
                              }}
                            >
                              <option value="">Select destination</option>
                              {(step.destinationOptions || []).map((destination) => (
                                <option key={destination.allianceId} value={destination.allianceId}>
                                  {destination.displayName} (projected {formatNumber(destination.projectedFill)}/{formatNumber(destination.capacity)})
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>
                        {(step.validationErrors || []).length > 0 ? (
                          <p className="md-planner-error">{step.validationErrors[0]}</p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : null}

                {overrideModal?.previewPayload?.finalSummary ? (
                  <div className="md-planner-conflict-chain">
                    <h4>Final Review</h4>
                    {(overrideModal.previewPayload.finalSummary.movementSummary || []).length > 0 ? (
                      <ul>
                        {(overrideModal.previewPayload.finalSummary.movementSummary || []).map((movement, index) => (
                          <li key={`${movement.commanderId}-${movement.targetAllianceId}-${index}`}>
                            {movement.commanderName || movement.commanderId} {'->'} {movement.targetAllianceName || movement.targetAllianceId}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    {(overrideModal.previewPayload.finalSummary.projectedTargets || []).length > 0 ? (
                      <div className="md-planner-capacity-table-wrap">
                        <table className="md-summary-table">
                          <thead>
                            <tr>
                              <th>Alliance</th>
                              <th>Projected Fill</th>
                              <th>Capacity</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(overrideModal.previewPayload.finalSummary.projectedTargets || []).map((target) => (
                              <tr key={`override-final-${target.allianceId}`}>
                                <td>{target.displayName}</td>
                                <td>{formatNumber(target.projectedFill)}</td>
                                <td>{formatNumber(target.capacity)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </>
            )}

            <div className="md-planner-conflict-actions">
              <AppButton
                className="md-primary-button"
                onClick={submitOverrideModal}
                disabled={
                  overrideModal.isLoadingPreview ||
                  overrideModal.isSubmitting ||
                  overrideModal?.previewPayload?.isResolved !== true ||
                  overrideModal?.previewPayload?.isBlocked === true
                }
              >
                {overrideModal.isSubmitting ? 'Applying...' : 'Apply Override'}
              </AppButton>
              <AppButton
                className="md-secondary-button"
                onClick={closeOverrideModal}
                disabled={overrideModal.isSubmitting}
              >
                Cancel
              </AppButton>
            </div>
          </div>
        </div>
      )}

      {notesModal.open && (
        <div className="md-notes-modal-backdrop" onClick={closeNotesModal}>
          <div className="md-notes-modal" onClick={(event) => event.stopPropagation()}>
            <div className="md-notes-modal-header">
              <h3>{notesModal.title}</h3>
              <AppButton className="md-secondary-button" onClick={closeNotesModal}>Close</AppButton>
            </div>

            <div className="md-notes-create">
              <label htmlFor="planner-new-note">New Note</label>
              <textarea
                id="planner-new-note"
                value={newNoteText}
                onChange={(event) => setNewNoteText(event.target.value)}
                rows={3}
                maxLength={2000}
                placeholder="Add note..."
              />
              <label className="md-planner-toggle">
                <input
                  type="checkbox"
                  checked={newNoteVisibleToOthers}
                  onChange={(event) => setNewNoteVisibleToOthers(event.target.checked)}
                />
                <span>Visible to others</span>
              </label>
              <AppButton className="md-primary-button" onClick={createNote} disabled={creatingNote || !newNoteText.trim()}>
                {creatingNote ? 'Saving...' : 'Add Note'}
              </AppButton>
            </div>

            <div className="md-notes-list">
              {isLoadingNotes ? <p>Loading notes...</p> : null}
              {!isLoadingNotes && notes.length === 0 ? <p>No notes yet.</p> : null}

              {notes.map((note) => {
                const noteId = String(note._id || '');
                const isEditing = editingNoteId === noteId;
                return (
                  <div key={noteId} className="md-note-item">
                    <div className="md-note-meta">
                      <span>
                        <strong>{note?.author?.username || 'Unknown'}</strong> · {note.createdAt ? new Date(note.createdAt).toLocaleString() : ''}
                      </span>
                      <span>{note.visibleToOthers ? 'Visible to others' : 'Private'}</span>
                    </div>

                    {isEditing ? (
                      <div className="md-note-edit">
                        <textarea
                          value={editingNoteText}
                          onChange={(event) => setEditingNoteText(event.target.value)}
                          rows={3}
                          maxLength={2000}
                        />
                        <label className="md-planner-toggle">
                          <input
                            type="checkbox"
                            checked={editingNoteVisibleToOthers}
                            onChange={(event) => setEditingNoteVisibleToOthers(event.target.checked)}
                          />
                          <span>Visible to others</span>
                        </label>
                        <div className="md-note-actions">
                          <AppButton className="md-primary-button" onClick={saveEditNote} disabled={!editingNoteText.trim()}>
                            Save
                          </AppButton>
                          <AppButton className="md-secondary-button" onClick={cancelEditNote}>
                            Cancel
                          </AppButton>
                        </div>
                      </div>
                    ) : (
                      <p className="md-note-text">{note.text}</p>
                    )}

                    {!isEditing && note.isAuthor ? (
                      <div className="md-note-actions">
                        <AppButton className="md-secondary-button" onClick={() => startEditNote(note)}>
                          Edit
                        </AppButton>
                        <AppButton className="md-secondary-button" onClick={() => deleteNote(noteId)}>
                          Delete
                        </AppButton>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {isLoadingPlan && !plan ? (
        <div className="md-card">
          <p>Loading consolidation plan...</p>
        </div>
      ) : null}
    </div>
  );
};

export default ConsolidationPlannerTab;

